import { BellIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router";
import { api } from "@/lib/api";
import { keys, useAction, useNotifications, useUsers } from "@/lib/queries";
import type { AppNotification } from "@/lib/types";
import { cn, timeAgo } from "@/lib/utils";
import { Avatar } from "./ui";
import { Popover } from "./ui/Popover";

export function entityPath(pid: string, type: string, id: string) {
  if (type === "epic") return `/p/${pid}/epics/${id}`;
  if (type === "sprint") return `/p/${pid}/sprints/${id}`;
  return `/p/${pid}/tasks/${id}`;
}

function describe(n: AppNotification, actor: string) {
  const item = n.data.key && n.data.number ? `${n.data.key}-${n.data.number} ${n.data.title}` : (n.data.title ?? "an item");
  switch (n.kind) {
    case "mentioned":
      return { head: `${actor} mentioned you in`, item, detail: n.data.snippet };
    case "commented":
      return { head: `${actor} commented on`, item, detail: n.data.snippet };
    case "assigned":
      return { head: `${actor} assigned you to`, item };
    case "unassigned":
      return { head: `${actor} removed you from`, item };
    case "stage_changed":
      return { head: `${actor} moved`, item, detail: `${n.data.from} → ${n.data.to}` };
    default:
      return { head: actor, item };
  }
}

export function NotificationsButton() {
  const notifications = useNotifications();
  const users = useUsers();
  const navigate = useNavigate();
  const read = useAction((id: string) => api.readNotification(id), { invalidate: [keys.notifications] });
  const readAll = useAction(() => api.readAllNotifications(), { invalidate: [keys.notifications] });
  const unread = notifications.data?.unread ?? 0;
  const nameOf = (id: string | null) => users.data?.find((u) => u.id === id)?.name ?? "Someone";

  return (
    <Popover
      placement="bottom-start"
      className="w-[360px] max-w-[calc(100vw-16px)]"
      trigger={(t) => (
        <button ref={t.ref} onClick={t.onClick} className="relative rounded-md p-1.5 text-muted hover:bg-hover hover:text-fg" aria-label="Notifications">
          <BellIcon className="size-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -end-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      )}
    >
      {(close) => (
        <div>
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="font-semibold">Inbox</span>
            {unread > 0 && (
              <button type="button" className="text-xs text-accent hover:underline" onClick={() => readAll.mutate()}>
                Mark all as read
              </button>
            )}
          </div>
          {notifications.data?.items.length === 0 && <div className="px-3 py-10 text-center text-sm text-muted">You're all caught up</div>}
          <div className="p-1">
            {notifications.data?.items.map((n) => {
              const d = describe(n, nameOf(n.actorId));
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    if (!n.readAt) read.mutate(n.id);
                    close();
                    navigate(entityPath(n.projectId, n.entityType, n.entityId));
                  }}
                  className={cn("flex w-full gap-2.5 rounded-md p-2 text-start hover:bg-hover", !n.readAt && "bg-accent/5")}
                >
                  <Avatar id={n.actorId ?? "x"} name={nameOf(n.actorId)} size={26} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] leading-snug">
                      <span className="text-muted">{d.head} </span>
                      <span className="font-medium" dir="auto">
                        {d.item}
                      </span>
                    </div>
                    {d.detail && (
                      <div className="mt-0.5 line-clamp-2 text-xs text-muted" dir="auto">
                        {d.detail}
                      </div>
                    )}
                    <div className="mt-0.5 text-[11px] text-faint">
                      {n.projectName} · {timeAgo(n.createdAt)}
                    </div>
                  </div>
                  {!n.readAt && <span className="mt-2 size-2 shrink-0 rounded-full bg-accent" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Popover>
  );
}
